import { Camera, QrCode } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  COMMON_NATIONALITIES,
  countryName,
  STATUSES,
  type PassportDraft,
  type PassportErrors,
} from "./passport";

/**
 * 1) パスポート (design/05 §3.3): typed by hand from the passport (phase 1). The camera (MRZ /
 * Visit Japan Web QR) comes later — the buttons say 「近日対応」; a passport scanner in keyboard
 * mode can type the two MRZ lines into the MRZ field (then only 在留資格 + 上陸年月日 are typed).
 */
export function PassportForm({
  value,
  errors,
  onChange,
  disabled,
}: {
  value: PassportDraft;
  errors: PassportErrors;
  onChange: (next: PassportDraft) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const locale = i18n.resolvedLanguage ?? "ja";
  const set = <K extends keyof PassportDraft>(key: K, next: PassportDraft[K]) =>
    onChange({ ...value, [key]: next });
  const upper = (text: string) => text.toUpperCase();

  const field = (
    key: "passportNumber" | "surname" | "givenName",
    label: string,
    options: { upper?: boolean; autoComplete?: string } = {},
  ) => (
    <label className="flex flex-col gap-1" htmlFor={`${id}-${key}`}>
      <span className="text-sm font-medium">{label}</span>
      <input
        id={`${id}-${key}`}
        value={value[key]}
        onChange={(e) => set(key, options.upper ? upper(e.target.value) : e.target.value)}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        disabled={disabled}
        aria-invalid={Boolean(errors[key])}
        aria-describedby={errors[key] ? `${id}-${key}-error` : undefined}
        className="min-h-12 rounded-md border px-3 font-mono text-lg uppercase aria-[invalid=true]:border-status-problem"
      />
      {errors[key] ? (
        <span id={`${id}-${key}-error`} className="text-sm text-pos-problem">
          {t(`taxfree.error_${errors[key]}`)}
        </span>
      ) : null}
    </label>
  );

  const dateField = (
    key: "dateOfBirth" | "entryDate" | "passportExpiry" | "departureDate",
    label: string,
  ) => (
    <label className="flex flex-col gap-1" htmlFor={`${id}-${key}`}>
      <span className="text-sm font-medium">{label}</span>
      <input
        id={`${id}-${key}`}
        type="date"
        value={value[key]}
        onChange={(e) => set(key, e.target.value)}
        disabled={disabled}
        aria-invalid={Boolean(errors[key])}
        className="min-h-12 rounded-md border px-3 text-lg aria-[invalid=true]:border-status-problem"
      />
      {errors[key] ? (
        <span className="text-sm text-pos-problem">{t(`taxfree.error_${errors[key]}`)}</span>
      ) : null}
    </label>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled
          title={t("taxfree.coming_soon")}
          className="flex min-h-12 items-center gap-2 rounded-md border px-4 opacity-60"
        >
          <Camera aria-hidden /> {t("taxfree.scan_mrz")} ({t("taxfree.coming_soon")})
        </button>
        <button
          type="button"
          disabled
          title={t("taxfree.coming_soon")}
          className="flex min-h-12 items-center gap-2 rounded-md border px-4 opacity-60"
        >
          <QrCode aria-hidden /> {t("taxfree.scan_vjw")} ({t("taxfree.coming_soon")})
        </button>
      </div>
      <details className="rounded-md border px-3 py-2">
        <summary className="min-h-10 cursor-pointer leading-10">{t("taxfree.mrz_reader")}</summary>
        <textarea
          aria-label={t("taxfree.mrz_label")}
          value={value.mrz}
          onChange={(e) => set("mrz", e.target.value.toUpperCase())}
          rows={2}
          spellCheck={false}
          disabled={disabled}
          className="mt-2 w-full rounded-md border px-3 py-2 font-mono text-base uppercase"
        />
        {errors.mrz ? (
          <span className="text-sm text-pos-problem">{t("taxfree.error_mrz")}</span>
        ) : null}
      </details>
      <div className="grid grid-cols-3 gap-3">
        {field("passportNumber", t("taxfree.passport_number"), { upper: true })}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium" htmlFor={`${id}-nationality`}>
            {t("taxfree.nationality")}
          </label>
          <input
            id={`${id}-nationality`}
            list={`${id}-countries`}
            value={value.nationality}
            onChange={(e) => set("nationality", upper(e.target.value).slice(0, 3))}
            autoComplete="off"
            disabled={disabled}
            aria-invalid={Boolean(errors.nationality)}
            className="min-h-12 rounded-md border px-3 font-mono text-lg uppercase aria-[invalid=true]:border-status-problem"
          />
          <datalist id={`${id}-countries`}>
            {COMMON_NATIONALITIES.map((code) => (
              <option key={code} value={code}>
                {countryName(code, locale)}
              </option>
            ))}
          </datalist>
          {value.nationality ? (
            <span className="text-sm text-muted-foreground">
              {countryName(value.nationality, locale)}
            </span>
          ) : null}
          {errors.nationality ? (
            <span className="text-sm text-pos-problem">{t("taxfree.error_nationality")}</span>
          ) : null}
        </div>
        {dateField("dateOfBirth", t("taxfree.date_of_birth"))}
        {field("surname", t("taxfree.surname"), { upper: true })}
        {field("givenName", t("taxfree.given_name"), { upper: true })}
        {dateField("entryDate", t("taxfree.entry_date"))}
      </div>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">{t("taxfree.status")}</legend>
        <div className="grid grid-cols-5 gap-2">
          {STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              aria-pressed={value.statusOfResidence === status}
              onClick={() => set("statusOfResidence", status)}
              disabled={disabled}
              className="min-h-pos-touch rounded-md border px-2 text-base aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
            >
              {t(`taxfree.status_${status}`)}
            </button>
          ))}
        </div>
      </fieldset>
      {value.statusOfResidence === "landing_permit" ? (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("taxfree.landing_permit_type")}</span>
          <input
            value={value.landingPermitType}
            onChange={(e) => set("landingPermitType", e.target.value)}
            disabled={disabled}
            className="min-h-12 rounded-md border px-3 text-lg"
          />
          {errors.landingPermitType ? (
            <span className="text-sm text-pos-problem">
              {t("taxfree.error_landing_permit_type")}
            </span>
          ) : null}
        </label>
      ) : null}
      {value.statusOfResidence === "jp_nonresident" ? (
        <div className="grid grid-cols-2 gap-3">
          <fieldset>
            <legend className="mb-1 text-sm font-medium">{t("taxfree.proof")}</legend>
            <div className="flex gap-2">
              {(["zairyu_shomei", "koseki_fuhyo"] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={value.proofKind === kind}
                  onClick={() => set("proofKind", kind)}
                  className="min-h-12 flex-1 rounded-md border px-2 aria-pressed:border-primary aria-pressed:bg-accent"
                >
                  {t(`taxfree.proof_${kind}`)}
                </button>
              ))}
            </div>
          </fieldset>
          {dateField("departureDate", t("taxfree.departure_date"))}
        </div>
      ) : null}
    </div>
  );
}
